/* ── API response types (matching hd_erp GraphQL schema) ──── */

export interface ApiFile {
    id: string;
    path: string;
    clientName?: string;
    clientSize?: number;
    clientType?: string;
}

export interface ApiSurface {
    id: string;
    short: string;
    nameDe: string;
    nameEn: string | null;
    /** Three.js roughness value (0 = mirror, 1 = fully rough) */
    averageRoughnessValue: number;
    roughnessFile: { id: string; path: string } | null;
    normalFile: { id: string; path: string } | null;
}

export interface ApiAlloyColor {
    id: string;
    short: string;
    nameDe: string;
    nameEn: string;
    hexColorValue: string;
}

export interface ApiAlloy {
    id: string;
    short: string;
    nameDe: string;
    nameEn: string;
    sortingIndex: number;
    color: ApiAlloyColor;
}

export interface ApiGemCut {
    id: string;
    nameDe: string;
    nameEn: string;
    shape: string;
    short: string;
}

export interface ApiGemClass {
    id: string;
    name: string;
    short: string;
    color: ApiAlloyColor;
}

export interface ApiGem {
    id: string;
    nameDe: string;
    nameEn: string;
    short: string;
    carat: number;
    diameter: number;
    width: number;
    length: number;
    height: number;
    cut: ApiGemCut;
}

export interface ApiModelFile {
    id: string;
    width: number;
    carat: number;
    file: ApiFile;
}

export interface ApiLayoutEntry {
    gemId: string;
    offsetX: number;
    offsetY: number;
}

export interface ApiLayoutDetails {
    strategy: string | null;
    padding: number | null;
    entries: ApiLayoutEntry[] | null;
}

export interface ApiGemSpacing {
    id: string;
    short: string;
    option: string;        // 'FIXED' | 'DISTRIBUTED'
    distance: number;
    spacingUnit: string;   // 'GEM' | 'MILLIMETER'
    minProductionDistance: number | null;
}

export interface ApiSettingType {
    id: string;
    short: string;
    styleType: string;   // 'flush' | 'canal' | 'prong' | ...
    layoutType: string;  // 'legacy' | 'starfield' | 'side' | ...
    orientation: string;
    layoutDetails: ApiLayoutDetails | null;
    spacings: ApiGemSpacing[];
    cut: { id: string; short: string; shape: string } | null;
    /** Gems filtered to fit this setting's cut and dimensions */
    allowedGems: ApiGem[];
}

export interface ApiRingProfile {
    id: string;
    name: string;
    short: string | null;
    labelDe: string | null;
    labelEn: string | null;
    formFactor: number;
    dxfFiles: { id: string; file: { id: string; path: string } }[];
    dxfLoopFiles: { id: string; file: { id: string; path: string } }[];
}

export interface ApiRingSegment {
    id: string;
    short: string;
    count: string;
    direction: string;
    primaryFormValue: number | null;
    secondaryFormValue: number | null;
    tertiaryFormValue: number | null;
}

export interface ApiRingPartial {
    id: string;
    index: number;
    name: string;
    fixedWidth: number | null;
    ringProfile: ApiRingProfile;
    ringSegments: ApiRingSegment[];
    alloys: ApiAlloy[];
    surfaces: ApiSurface[];
    gems: ApiGem[];
    gemClasses: ApiGemClass[];
    settingTypes: ApiSettingType[];
}

export interface ApiRingTemplate {
    id: string;
    short: string;
    nameDe: string;
    nameEn: string;
    descriptionDe: string | null;
    descriptionEn: string | null;
    sortingIndex: number;
    gender: string | null;
    multipart: boolean | null;
    modelFiles: ApiModelFile[];
    ringPartials: ApiRingPartial[];
}

/* ── Configurator option types ──────────────────────────────── */

export interface GlowColorOption {
    id: string;
    label: string;
    /** CSS hex / hsl */
    cssValue: string;
    /** Three.js hex number */
    threeHex: number;
}

export const GLOW_COLORS: GlowColorOption[] = [
    { id: 'cyan',   label: 'Cyan',   cssValue: '#00f5ff', threeHex: 0x00f5ff },
    { id: 'violet', label: 'Violet', cssValue: '#bf00ff', threeHex: 0xbf00ff },
    { id: 'lime',   label: 'Lime',   cssValue: '#b0ff00', threeHex: 0xb0ff00 },
    { id: 'rose',   label: 'Rose',   cssValue: '#ff0066', threeHex: 0xff0066 },
    { id: 'amber',  label: 'Amber',  cssValue: '#ff9500', threeHex: 0xff9500 },
];
